import { type HTMLAttributes, useMemo, useState } from 'react';
import { cn } from '@virginexperiencedays/tailwind';

import { SectionHeading } from '@virginexperiencedays/components-v2/src/typography/SectionHeading';
import { CollectionChoiceCard } from '@virginexperiencedays/components-v2/src/cards/CollectionChoice';
import {
  AccordionGroup,
  AccordionItem,
  AccordionHeading,
  AccordionContent,
} from '@virginexperiencedays/components-v2/src/layout/AccordionV2';

import { DataTestId } from '../VariationPage/PageUI';
import { CollectionChoiceOverlay } from '../CollectionChoice';
import { useFetchCollectionChoiceInfo } from '../hooks/useFetchCollectionChoiceInfo';

import type { CollectionProductChoices, ProductChoice } from '../VariationPage/extractors';
import type { Product } from '@virginexperiencedays/products';

import {
  PdpInteraction,
  PdpModules,
  trackPdp,
  trackPdpCollectionChoice,
  type PdpInteractionProps,
} from '../../../../libs/tracking/pdp';

/**
 * The CollectionProductChoicesWidget  component is made up of a number of sections currently
 * specific to the `/collection/*` pages. It is wrapped in a `div` as a fully self-contained component.
 * It should only be rendered when the `isCollectionProduct` prop is `true`.
 */

type CollectionProductChoicesWidgetProps = {
  productTitle: string;
  collections: CollectionProductChoices[];
  choicesCount: number;
  className?: string;
  trackingData?: PdpInteractionProps;
} & HTMLAttributes<HTMLDivElement>;

export function CollectionProductChoicesWidget({
  productTitle,
  collections,
  choicesCount,
  className,
  trackingData,
}: CollectionProductChoicesWidgetProps) {
  // By Default open first AccordionItem
  const defaultOpen = useMemo(() => {
    return collections?.length ? collections.map((_, index) => index <= 0) : [];
  }, [collections]);

  const [isCollectionChoiceOpen, setIsCollectionChoiceOpen] = useState(false);
  const [isAccordionItemOpen, setIsAccordionItemOpen] = useState(defaultOpen);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(true);

  const {
    collectionChoiceInfo,
    isLoadingCollectionChoiceInfo,
    setIsLoadingCollectionChoiceInfo,
    triggerFetchCollectionChoiceInfo,
    clearCollectionChoiceInfo,
  } = useFetchCollectionChoiceInfo();

  const handleToggle = (index: number, title: string) => {
    setIsAccordionItemOpen((prev) => {
      const updated = [...prev];
      const willBeOpen = !updated[index];

      // Track accordion interaction
      if (trackingData) {
        trackPdp({
          ...trackingData,
          module: PdpModules.CollectionChoicesWidget,
          moduleTitle: title,
          interaction: willBeOpen ? PdpInteraction.ClickReadMore : PdpInteraction.ClickReadLess,
        });
      }

      updated[index] = willBeOpen;
      return updated;
    });
  };

  const handleHowItWorksToggle = () => {
    setIsHowItWorksOpen((value) => {
      const willBeOpen = !value;

      // Track "How it works" accordion interaction
      if (trackingData) {
        trackPdp({
          ...trackingData,
          module: PdpModules.AdditionalDetails,
          moduleTitle: 'How it works',
          interaction: willBeOpen ? PdpInteraction.ClickReadMore : PdpInteraction.ClickReadLess,
        });
      }

      return willBeOpen;
    });
  };

  const handleOpenCollectionChoice = (slug?: string) => {
    if (!slug?.length) return;
    setIsLoadingCollectionChoiceInfo(true);
    setIsCollectionChoiceOpen(true);
    void triggerFetchCollectionChoiceInfo(slug);
  };

  const handleCloseCollectionChoiceOverlay = () => {
    clearCollectionChoiceInfo();
  };

  const handleChoiceClick = (choice: ProductChoice) => {
    // Track collection choice interaction
    if (trackingData) {
      trackPdpCollectionChoice({
        ...trackingData,
        module: PdpModules.CollectionChoicesWidget,
        interaction: PdpInteraction.ClickCollectionChoice,
        collectionChoiceSlug: choice.slug,
        collectionChoiceTitle: choice.title,
      });
    }

    handleOpenCollectionChoice(choice.slug);
  };

  if (!collections.length) return null;

  return (
    <>
      <div
        data-testid={DataTestId.CollectionWrapper}
        className={cn('relative grid grid-cols-1 gap-6', className)}
      >
        <section
          data-testid={DataTestId.CollectionTitleSummary}
          className="flex flex-col gap-6 lg:col-start-1"
        >
          <AccordionItem data-testid={DataTestId.AccordionItem}>
            <AccordionHeading isOpen={isHowItWorksOpen} onClick={handleHowItWorksToggle}>
              <SectionHeading>How it works</SectionHeading>
            </AccordionHeading>
            <AccordionContent isOpen={isHowItWorksOpen}>
              <p className="text-base">
                All you have to do is pay for the product you wish to purchase and we&apos;ll send a
                gift pack to you or directly to the recipient. A voucher and full activation details
                will be included in the pack.
              </p>
              <p className="pt-6 text-base">
                All images shown are for illustration purpose only, actual product may vary from
                images shown.
              </p>
            </AccordionContent>
          </AccordionItem>
        </section>

        <section
          data-testid={DataTestId.CollectionChoices}
          className="flex flex-col gap-6 lg:col-start-1"
        >
          <div>
            <SectionHeading>Great choices within this collection</SectionHeading>
            <p className="pt-4 text-base">
              <span className="font-bold">{choicesCount} experiences</span> to discover, with
              activities to suit whoever you&apos;re shopping for.
            </p>
          </div>

          <AccordionGroup data-testid={DataTestId.Accordion}>
            {collections.map((collection, index) => {
              if (!collection?.choices?.length) return null;

              const isItemOpen = isAccordionItemOpen[index];
              const title = collection.collectionTitle;
              return (
                <AccordionItem
                  key={collection.collectionTitle}
                  data-testid={DataTestId.AccordionItem}
                  className="py-5"
                >
                  <AccordionHeading
                    isOpen={isItemOpen}
                    onClick={() => handleToggle(index, title)}
                    aria-label={`${title} section containing ${collection.choices.length} ${
                      collection.choices.length !== 1 ? 'Experiences' : 'Experience'
                    }`}
                  >
                    <CollectionHeading title={title} count={collection.choices.length} />
                  </AccordionHeading>
                  <AccordionContent isOpen={isItemOpen}>
                    <div className="mt-2 inline-grid grid-cols-2 gap-4 py-1 sm:grid-cols-4 md:grid-cols-5">
                      {collection.choices.map((choice) => {
                        return (
                          <CollectionChoiceCard
                            key={`${collection.collectionTitle}-${choice.title}`}
                            className="h-full w-full max-w-full hover:cursor-pointer"
                            onClick={() => handleChoiceClick(choice)}
                            aria-label={choice.title}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleChoiceClick(choice);
                              }
                            }}
                            title={choice.title}
                            image={{
                              src: choice.mainImageUrl,
                              alt: choice.mainImageAltText,
                            }}
                          />
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </AccordionGroup>
        </section>
      </div>
      <CollectionChoiceOverlay
        collectionTitle={productTitle}
        isOpen={isCollectionChoiceOpen}
        onClose={handleCloseCollectionChoiceOverlay}
        // TODO LP-7094: use an interface and migrate off old Product API types
        product={collectionChoiceInfo as unknown as Product}
        isLoadingCollectionChoiceInfo={isLoadingCollectionChoiceInfo}
        setIsOpen={setIsCollectionChoiceOpen}
        trackingData={trackingData}
      />
    </>
  );
}

type CollectionHeadingProps = {
  title: string;
  count: number;
};

function CollectionHeading({ title, count }: Readonly<CollectionHeadingProps>) {
  return (
    <div className={cn('flex flex-row flex-wrap items-center')}>
      <div className="max-w-max py-1">
        <SectionHeading>{title}</SectionHeading>
      </div>
      <div className={cn('text-md max-w-max pl-3')}>
        ({count} {count !== 1 ? 'Experiences' : 'Experience'})
      </div>
    </div>
  );
}
